// GC94 · R02 — EXPEDIENTE OPERATIVO de enfermería (sólo lectura).
// Se alimenta de GET /api/personas/{id}/ficha (detalle autorizado por ID), NUNCA de la tarjeta
// reducida del listado. No ofrece edición, no abre rutas administrativas ni la libreta global.
// C2: los bloques informativos vienen del módulo compartido con el Radar (BloquesOperativos).
import { Button } from "@/components/ui/button";
import { fmtFecha } from "@/lib/format";
import {
  BloqueContexto, BloquePlan, BloqueContactos, BloqueEquipos, BloqueEjes,
  etiquetaModalidad, valor,
} from "@/components/persona/BloquesOperativos";
import {
  ArrowLeft, User2, MapPin, ClipboardList, Phone, Cpu, ShieldCheck, Loader2,
} from "lucide-react";

function Dato({ label, value, testId }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</p>
      <p data-testid={testId} className="text-sm text-slate-700 mt-1 break-words [overflow-wrap:anywhere] whitespace-pre-wrap">{valor(value)}</p>
    </div>
  );
}

function Card({ title, icon: Icon, children, testId }) {
  return (
    <section data-testid={testId} className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 space-y-4">
      <h3 className="text-base font-medium text-[#012472] flex items-center gap-2">
        {Icon && <Icon strokeWidth={1.6} className="h-4 w-4" />} {title}
      </h3>
      {children}
    </section>
  );
}

function Volver({ onBack }) {
  return (
    <button onClick={onBack} data-testid="persona-back" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#012472] transition-colors">
      <ArrowLeft strokeWidth={1.5} className="h-4 w-4" /> Volver al sitio
    </button>
  );
}

export default function PersonaFichaOperativa({ ficha, estado, onBack }) {
  if (estado === "cargando" || (!ficha && !estado)) {
    return (
      <div className="tema-noche-auto space-y-5">
        <Volver onBack={onBack} />
        <p data-testid="ficha-operativa-cargando" className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Cargando el expediente autorizado…</p>
      </div>
    );
  }
  if (!ficha) {
    return (
      <div className="tema-noche-auto space-y-5">
        <Volver onBack={onBack} />
        <p data-testid="ficha-operativa-error" className="text-sm text-slate-600">
          {estado === "no_disponible"
            ? "Este expediente no está disponible para tu acceso autorizado."
            : "No se pudo cargar el expediente. Vuelve a intentarlo; no hay nada que volver a capturar."}
        </p>
        <Button variant="outline" size="sm" className="rounded-lg" data-testid="ficha-operativa-volver" onClick={onBack}>Volver al sitio</Button>
      </div>
    );
  }

  const id = ficha.identidad || {};
  const at = ficha.atencion;
  const ubicacion = [id.habitacion, id.cama_etiqueta ? `Cama ${id.cama_etiqueta}` : null]
    .filter(Boolean).join(" · ");

  return (
    <div className="tema-noche-auto space-y-5" data-testid={`persona-ficha-operativa-${id.id}`}>
      <Volver onBack={onBack} />

      <div>
        {/* Nombre COMPLETO legible también a 375–390 px: salta de línea, no se recorta. */}
        <h1 data-testid="persona-object-nombre" className="text-2xl font-semibold tracking-tight text-[#012472] break-words [overflow-wrap:anywhere]">
          {id.nombre || "(sin nombre)"}
        </h1>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500">
          <span className="flex items-center gap-1"><MapPin strokeWidth={1.5} className="h-3.5 w-3.5" /> {id.sitio_nombre || "—"}</span>
          {ubicacion && <span data-testid="persona-ubicacion">· {ubicacion}</span>}
          <span data-testid="persona-modalidad">· {etiquetaModalidad(id)}</span>
          {id.estado === "Inactiva" && <span className="rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 text-xs">Baja</span>}
        </p>
      </div>

      {at && (
        <div data-testid="persona-caso-activo" className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Caso en curso en este sitio: {at.prioridad_label || at.prioridad} · {at.estado}
          {at.operador ? ` · atiende ${at.operador}` : ""}
        </div>
      )}

      <Card title="Identidad y ubicación" icon={User2} testId="ficha-identidad">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Dato label="Nombre completo" value={id.nombre} testId="dato-nombre" />
          <Dato label="Fecha de nacimiento" value={id.fecha_nacimiento ? `${fmtFecha(id.fecha_nacimiento)}${id.edad ? ` · ${id.edad} años` : ""}` : id.fecha_nacimiento} testId="dato-nacimiento" />
          <Dato label="Idioma preferido" value={id.idioma_preferido} testId="dato-idioma" />
          <Dato label="Teléfono del residente" value={id.telefono} testId="dato-telefono" />
          <Dato label="Habitación" value={id.habitacion} testId="dato-habitacion" />
          <Dato label="Cama" value={id.cama_etiqueta} testId="dato-cama" />
        </div>
      </Card>

      <Card title="Contexto de atención" icon={ClipboardList} testId="ficha-contexto">
        <BloqueContexto contexto={ficha.atencion_contexto} instrucciones={ficha.instrucciones}
          contextoHabitacion={ficha.contexto_habitacion} testId="ficha-contexto-bloque" />
      </Card>

      <Card title="Plan de respuesta vigente" icon={ShieldCheck} testId="ficha-plan">
        <BloquePlan plan={ficha.plan_vigente} testId="ficha-plan-bloque" />
      </Card>

      <Card title="Contactos de atención" icon={Phone} testId="ficha-contactos">
        <BloqueContactos contactos={ficha.contactos} testId="ficha-contactos-bloque" />
      </Card>

      <Card title="Cobertura técnica" icon={Cpu} testId="ficha-dispositivos">
        <BloqueEquipos dispositivos={ficha.dispositivos} testId="ficha-equipos-bloque" />
      </Card>

      <Card title="Preparación (readiness)" icon={ShieldCheck} testId="ficha-readiness">
        <BloqueEjes readiness={ficha.readiness} identidad={id} testId="ficha-ejes-bloque" />
      </Card>
    </div>
  );
}
